import "./SwipeableModals.scss";
import { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import AddPhoto from "../../../assets/add-photo.png";
import BlueButton from "../../../components/BlueButton/BlueButton";
import { toast } from "react-toastify";
import api from "../../../utils/api";
import DeleteIcon from "@mui/icons-material/Delete";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  type,
  initialData = null,
  refreshData,
  isSubDepartment = false,
  isInnerDepartment = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState({
    title: { ar: "", en: "" },
    image: null,
    active: 0,
    type: type,
    parent_id: null,
    flag: 1, // Default flag for main department
  });
  const [mainDepartments, setMainDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingSubDepartments, setLoadingSubDepartments] = useState(false);

  // Fetch main departments if we're in subdepartment mode
  useEffect(() => {
    if ((isSubDepartment || isInnerDepartment) && open) {
      fetchMainDepartments();
    }
  }, [isSubDepartment, isInnerDepartment, open, type]);

  // Fetch sub departments if we're in inner department mode and have a selected main department
  useEffect(() => {
    if (isInnerDepartment && open && category.parent_id) {
      fetchSubDepartments(category.parent_id);
    }
  }, [isInnerDepartment, open, category.parent_id]);

  const fetchMainDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const params = new URLSearchParams({
        per_page: 100,
        type,
        active: 1, // Only fetch active departments for parent selection
        flag: 1, // Only fetch main departments
      });
      const res = await api.get(`/categories?${params.toString()}`);
      setMainDepartments(res.data.data || []);
    } catch (error) {
      toast.error("Failed to load main departments");
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchSubDepartments = async (parentId) => {
    if (!parentId) return;

    setLoadingSubDepartments(true);
    try {
      const params = new URLSearchParams({
        per_page: 100,
        type,
        active: 1, // Only fetch active departments
        flag: 2, // Only fetch sub departments
        parent_id: parentId,
      });
      const res = await api.get(`/categories?${params.toString()}`);
      setSubDepartments(res.data.data || []);
    } catch (error) {
      toast.error("Failed to load sub departments");
    } finally {
      setLoadingSubDepartments(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      if (
        currentView === "editDepartment" ||
        currentView === "editSubDepartment" ||
        currentView === "editInnerDepartment"
      ) {
        // Map the API response format to our component's state format
        setCategory({
          ...initialData,
          title: {
            // Look for either nested title object or title_ar/title_en fields
            ar: initialData.title?.ar || initialData.title_ar || "",
            en: initialData.title?.en || initialData.title_en || "",
          },
          image: initialData.image || null,
          parent_id: initialData.parent_id || null,
          sub_parent_id: initialData.sub_parent_id || null,
          type: initialData.type || type,
          active: initialData.active !== undefined ? initialData.active : 0,
          flag:
            initialData.flag ||
            (isInnerDepartment ? 3 : isSubDepartment ? 2 : 1),
        });

        // If editing inner department, fetch sub departments based on parent_id
        if (currentView === "editInnerDepartment" && initialData.parent_id) {
          fetchSubDepartments(initialData.parent_id);
        }
      }
    } else {
      // Set the appropriate flag based on department type
      const flag = isInnerDepartment ? 3 : isSubDepartment ? 2 : 1;

      setCategory({
        title: { ar: "", en: "" },
        image: null,
        active: 0,
        type,
        parent_id: null,
        sub_parent_id: null,
        flag,
      });
    }
  }, [initialData, currentView, type, isSubDepartment, isInnerDepartment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate parent_id for subdepartments
    if (isSubDepartment && !category.parent_id) {
      toast.error("يرجى اختيار القسم الرئيسي");
      return;
    }
    // Validate parent_id and sub_parent_id for inner departments
    if (isInnerDepartment) {
      if (!category.parent_id) {
        toast.error("يرجى اختيار القسم الرئيسي");
        return;
      }
      if (!category.sub_parent_id) {
        toast.error("يرجى اختيار القسم الفرعي");
        return;
      }
    }
    // Validate required fields
    if (!category.title.ar || !category.title.en) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setIsLoading(true);
    try {
      const isCreate =
        currentView === "createDepartment" ||
        currentView === "createSubDepartment" ||
        currentView === "createInnerDepartment";

      const isEditSubDepartment = currentView === "editSubDepartment";
      const isEditInnerDepartment = currentView === "editInnerDepartment";

      // Always use FormData for both create and edit operations
      const formData = new FormData();

      // Title fields are always required
      formData.append("title[ar]", category.title.ar);
      formData.append("title[en]", category.title.en);

      // Only send type, active, and flag in create operations, not in edit operations
      if (isCreate) {
        formData.append("type", type);
        formData.append("active", String(category.active));

        // Add flag based on department type
        const flag = isInnerDepartment ? 3 : isSubDepartment ? 2 : 1;
        formData.append("flag", String(flag));
      }

      // Add image only if it's a File object
      if (category.image instanceof File) {
        formData.append("image", category.image);
      }

      // For editInnerDepartment, backend expects sub_parent_id to be sent as parent_id
      if (isEditInnerDepartment && category.sub_parent_id) {
        formData.append("parent_id", category.sub_parent_id);
      }
      // For all other cases, append parent_id normally when needed
      else if (
        category.parent_id &&
        (((isSubDepartment || isInnerDepartment) && isCreate) ||
          isEditSubDepartment)
      ) {
        formData.append("parent_id", category.parent_id);
      }

      // Append sub_parent_id only for create operations of inner departments
      // (For editInnerDepartment we send it as parent_id instead)
      if (isInnerDepartment && category.sub_parent_id && isCreate) {
        formData.append("sub_parent_id", category.sub_parent_id);
      }

      const endpoint = "/categories";
      const itemId = initialData?.id || "";

      // Use the same endpoint pattern for both create and update
      const url = isCreate ? endpoint : `${endpoint}/${itemId}`;

      await api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Show success message based on operation and department type
      const operationType = isCreate ? "إنشاء" : "تحديث";
      const departmentType = isInnerDepartment
        ? "القسم الداخلي"
        : isSubDepartment
        ? "القسم الفرعي"
        : "القسم";

      toast.success(`تم ${operationType} ${departmentType} بنجاح`);

      refreshData?.();
      onClose();
    } catch (error) {
      console.error("API Error:", error);
      toast.error(
        error.response?.data?.message || error.message || "حدث خطأ ما"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getFormTitle = () => {
    if (isInnerDepartment) {
      return currentView.includes("create")
        ? "اضافة قسم داخلي جديد"
        : "تعديل القسم الداخلي";
    }
    if (isSubDepartment) {
      return currentView.includes("create")
        ? "اضافة قسم فرعي جديد"
        : "تعديل القسم الفرعي";
    }
    return currentView.includes("create") ? "اضافة قسم جديد" : "تعديل القسم";
  };

  const handleMainDepartmentChange = (e) => {
    const parentId = e.target.value;
    setCategory({
      ...category,
      parent_id: parentId,
      // Reset sub_parent_id when main department changes
      sub_parent_id: null,
    });

    if (isInnerDepartment && parentId) {
      fetchSubDepartments(parentId);
    }
  };

  const renderForm = () => (
    <div className="user-modal">
      <p className="user-modal-p">{getFormTitle()}</p>
      <form onSubmit={handleSubmit}>
        <div className="form-img">
          <label htmlFor="image" className="img-control">
            <img
              src={
                category.image instanceof File
                  ? URL.createObjectURL(category.image)
                  : category.image || AddPhoto
              }
              alt=""
            />
            <span>اضافة صورة الشعار</span>
            {category.image && (
              <IconButton
                className="remove-image-btn"
                onClick={(e) => {
                  e.preventDefault();
                  setCategory({ ...category, image: null });
                }}
                size="small"
                sx={{
                  position: "absolute",
                  top: "5px",
                  right: "5px",
                  bgcolor: "rgba(255,255,255,0.8)",
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </label>
          <input
            type="file"
            id="image"
            onChange={(e) =>
              setCategory({
                ...category,
                image: e.target.files ? e.target.files[0] : null,
              })
            }
          />
        </div>

        {/* Main Department Selector for Sub and Inner Departments */}
        {(isSubDepartment || isInnerDepartment) &&
          currentView !== "editInnerDepartment" && (
            <FormControl sx={{ width: "100%" }} variant="outlined" required>
              <InputLabel htmlFor="parent-id">القسم الرئيسي</InputLabel>
              <Select
                value={category.parent_id || ""}
                onChange={handleMainDepartmentChange}
                label="القسم الرئيسي"
                disabled={loadingDepartments}
                error={
                  (isSubDepartment || isInnerDepartment) &&
                  !category.parent_id &&
                  !loadingDepartments
                }
              >
                {loadingDepartments ? (
                  <MenuItem disabled value="">
                    جاري تحميل الأقسام...
                  </MenuItem>
                ) : mainDepartments.length > 0 ? (
                  mainDepartments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.title_ar || dept.title?.ar || ""}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    لا توجد أقسام رئيسية
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}

        {/* Sub Department Selector for Inner Departments */}
        {isInnerDepartment && (
          <FormControl sx={{ width: "100%" }} variant="outlined" required>
            <InputLabel htmlFor="sub-parent-id">القسم الفرعي</InputLabel>
            <Select
              value={category.sub_parent_id || ""}
              onChange={(e) =>
                setCategory({ ...category, sub_parent_id: e.target.value })
              }
              label="القسم الفرعي"
              disabled={loadingSubDepartments || !category.parent_id}
              error={
                isInnerDepartment &&
                !category.sub_parent_id &&
                !loadingSubDepartments &&
                category.parent_id
              }
            >
              {!category.parent_id ? (
                <MenuItem disabled value="">
                  يرجى اختيار القسم الرئيسي أولاً
                </MenuItem>
              ) : loadingSubDepartments ? (
                <MenuItem disabled value="">
                  جاري تحميل الأقسام الفرعية...
                </MenuItem>
              ) : subDepartments.length > 0 ? (
                subDepartments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.title_ar || dept.title?.ar || ""}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  لا توجد أقسام فرعية
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel htmlFor="title-ar" sx={{ backgroundColor: "white" }}>
            اسم القسم بالعربية
          </InputLabel>
          <OutlinedInput
            required
            id="title-ar"
            type="text"
            value={category.title.ar}
            onChange={(e) =>
              setCategory({
                ...category,
                title: { ...category.title, ar: e.target.value },
              })
            }
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel htmlFor="title-en" sx={{ backgroundColor: "white" }}>
            اسم القسم بالانجليزية
          </InputLabel>
          <OutlinedInput
            required
            id="title-en"
            type="text"
            value={category.title.en}
            onChange={(e) =>
              setCategory({
                ...category,
                title: { ...category.title, en: e.target.value },
              })
            }
          />
        </FormControl>

        <BlueButton
          text={currentView.includes("create") ? "اضافة" : "تحديث"}
          size="full"
          variant="fill"
          loading={isLoading}
          type="submit"
        />
      </form>
    </div>
  );

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {renderForm()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;
